/**
 * +field: Auto mode's rules (docs/design/zones.md "Auto"), DOM- and sim-free so they are unit-tested.
 *
 *  - Z / the HUD button toggles it; it only runs where there is something to hunt (fields; also a
 *    levelled dungeon, where the brain plays like a party bot). In town it refuses with a reason.
 *  - Any manual movement or cast input turns it off (like WoW auto-run).
 *  - While it is on, portal events are suppressed (it never changes map), and it never turns in
 *    bounties or leaves the field (those are player actions in town panels).
 */
import type { AutoStatus, MapKind } from './fieldView';

export interface AutoHero {
  alive: boolean;
  inCombat: boolean;
  hp: number;
  maxHp: number;
}

/** Below this HP fraction out of combat the hero is shown as resting (fields regenerate fast). */
export const AUTO_REST_FRAC = 0.6;

export class AutoMode {
  private on = false;
  /** Why the last toggle refused / what last turned it off (HUD hint), or null. */
  reason: string | null = null;
  /** Bumped on every change (HUD redraw). */
  version = 0;

  /** +zone: the hero stands in a safe area (the town on a zone map). */
  private safe = false;

  constructor(private mapKind: MapKind) {}

  get active(): boolean {
    return this.on;
  }

  /** Auto can run here at all (not on a town map, not in a safe area). */
  get available(): boolean {
    return this.mapKind !== 'town' && !this.safe;
  }

  /** +zone: the hero entered / left a safe area. */
  setSafe(safe: boolean): void {
    if (this.safe === safe) return;
    this.safe = safe;
    this.version++;
  }

  /** The map changed (Auto stays as it was where it is allowed, else turns off). */
  setMap(kind: MapKind): void {
    this.mapKind = kind;
    if (!this.available && this.on) this.set(false, null);
  }

  /** Toggle; returns the new state. Refuses (stays off, sets `reason`) where nothing can be hunted. */
  toggle(): boolean {
    if (this.on) return this.set(false, null);
    if (!this.available) {
      this.reason = 'Auto hunts in the wilds, not in town';
      this.version++;
      return false;
    }
    return this.set(true, null);
  }

  /** Manual movement / cast input: Auto hands control back. Returns true if it was on. */
  manual(kind: 'move' | 'cast'): boolean {
    if (!this.on) return false;
    this.set(false, kind === 'move' ? 'Auto off: you moved' : 'Auto off: you took control');
    return true;
  }

  /** Portals are ignored while Auto drives (it never changes map). */
  suppressPortal(): boolean {
    return this.on;
  }

  /** What the badge says the hero is doing. */
  status(hero: AutoHero | null): AutoStatus {
    if (!this.on) return 'off';
    if (!hero) return 'paused';
    if (!hero.alive) return 'reviving';
    if (hero.inCombat) return 'fighting';
    if (hero.maxHp > 0 && hero.hp < hero.maxHp * AUTO_REST_FRAC) return 'resting';
    return 'hunting';
  }

  private set(on: boolean, reason: string | null): boolean {
    this.on = on;
    this.reason = reason;
    this.version++;
    return on;
  }
}

export const AUTO_STATUS_TEXT: Readonly<Record<AutoStatus, string>> = {
  off: '',
  hunting: 'Hunting',
  fighting: 'Fighting',
  resting: 'Recovering',
  reviving: 'Reviving',
  paused: 'Waiting',
  gathering: 'Gathering', // +prof
};
